import { supabase } from './supabaseClient';
import type { AdminStatsData, ClientStats, CallerStats, RdvWithDetails, CampaignComment } from '../../types/admin';

export const adminService = {
  // Récupérer tous les RDV avec les détails des campagnes et clients
  async getAllRdvWithDetails(): Promise<RdvWithDetails[]> {
    const { data: leads, error } = await supabase
      .from('leads')
      .select(`
        id,
        prenom,
        nom,
        entreprise,
        email,
        telephone,
        rdv,
        rdv_date,
        rdv_commentaire,
        rdv_source,
        etat_rdv,
        prospection_etape,
        retour_client,
        created_at,
        updated_at,
        campaign_id,
        campaigns!inner(
          id,
          nom,
          user_id
        )
      `)
      .eq('rdv', true)
      .order('rdv_date', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des RDV:', error);
      throw error;
    }

    // Récupérer les profils des clients séparément
    const userIds = Array.from(new Set(leads?.map((lead: any) => lead.campaigns.user_id) || []));
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, full_name, client_name, email')
      .in('id', userIds);

    if (profilesError) {
      console.error('Erreur lors de la récupération des profils:', profilesError);
      throw profilesError;
    }

    const profilesMap = new Map(profiles?.map((p: any) => [p.id, p]) || []);

    return leads?.map((lead: any) => {
      const profile = profilesMap.get(lead.campaigns.user_id);
      return {
        id: lead.id,
        prenom: lead.prenom,
        nom: lead.nom,
        entreprise: lead.entreprise,
        email: lead.email,
        telephone: lead.telephone,
        rdv_date: lead.rdv_date!,
        rdv_commentaire: lead.rdv_commentaire,
        rdv_source: lead.rdv_source as 'Mail' | 'LinkedIn' | 'Phone',
        etat_rdv: lead.etat_rdv!,
        prospection_etape: lead.prospection_etape!,
        retour_client: lead.retour_client,
        campaign_id: lead.campaign_id,
        campaign_name: lead.campaigns.nom,
        client_id: lead.campaigns.user_id,
        client_name: profile?.client_name || profile?.full_name || 'N/A',
        client_email: profile?.email || 'N/A',
        created_at: lead.created_at!,
        updated_at: lead.updated_at,
      };
    }) || [];
  },

  // Récupérer les statistiques globales pour les admins
  async getAdminStats(): Promise<AdminStatsData> {
    // Récupérer tous les RDV
    const { data: rdvLeads, error: rdvError } = await supabase
      .from('leads')
      .select('rdv, etat_rdv, rdv_date')
      .eq('rdv', true);

    if (rdvError) throw rdvError;

    // Calculer les stats
    const totalRdv = rdvLeads?.length || 0;
    const rdvValidated = rdvLeads?.filter((l: any) => l.etat_rdv === 'Validé').length || 0;
    const rdvPending = rdvLeads?.filter((l: any) => l.etat_rdv === 'En attente').length || 0;
    const rdvLitige = rdvLeads?.filter((l: any) => l.etat_rdv === 'Litige').length || 0;
    const rdvNoShow = rdvLeads?.filter((l: any) => l.etat_rdv === 'No show').length || 0;
    const conversionRate = totalRdv > 0 ? (rdvValidated / totalRdv) * 100 : 0;

    // RDV ce mois-ci
    const currentMonth = new Date().toISOString().slice(0, 7);
    const rdvThisMonth = rdvLeads?.filter((l: any) => 
      l.rdv_date && l.rdv_date.startsWith(currentMonth)
    ).length || 0;

    // Récupérer le nombre d'utilisateurs actifs
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, is_active, role')
      .eq('is_active', true);

    if (profilesError) throw profilesError;

    const activeClients = profiles?.filter((p: any) => p.role === 'client' || !p.role).length || 0;
    const activeCallers = profiles?.filter((p: any) => p.role === 'caller').length || 0;

    return {
      totalRdv,
      rdvValidated,
      rdvPending,
      rdvLitige,
      rdvNoShow,
      conversionRate: parseFloat(conversionRate.toFixed(2)),
      rdvThisMonth,
      activeClients,
      activeCallers,
    };
  },

  // Récupérer les statistiques par client
  async getClientStats(): Promise<ClientStats[]> {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select(`
        id,
        full_name,
        client_name,
        email,
        updated_at,
        is_active
      `)
      .eq('is_active', true)
      .in('role', ['client', 'admin']);

    if (error) throw error;

    // Récupérer les campagnes et leads séparément
    const { data: campaigns, error: campaignsError } = await supabase
      .from('campaigns')
      .select(`
        id,
        nom,
        statut,
        user_id,
        leads(id, rdv, etat_rdv, rdv_date)
      `);

    if (campaignsError) throw campaignsError;

    const campaignsByUser = new Map();
    campaigns?.forEach((campaign: any) => {
      if (!campaignsByUser.has(campaign.user_id)) {
        campaignsByUser.set(campaign.user_id, []);
      }
      campaignsByUser.get(campaign.user_id).push(campaign);
    });

    return profiles?.map((profile: any) => {
      const userCampaigns = campaignsByUser.get(profile.id) || [];
      const totalCampaigns = userCampaigns.length;
      const activeCampaigns = userCampaigns.filter((c: any) => c.statut === 'Lancée' || c.statut === 'En cours').length;
      
      const allLeads = userCampaigns.flatMap((c: any) => c.leads || []);
      const rdvLeads = allLeads.filter((l: any) => l.rdv);
      const totalRdv = rdvLeads.length;
      const rdvValidated = rdvLeads.filter((l: any) => l.etat_rdv === 'Validé').length;
      const rdvPending = rdvLeads.filter((l: any) => l.etat_rdv === 'En attente').length;
      const rdvNoShow = rdvLeads.filter((l: any) => l.etat_rdv === 'No show').length;
      const rdvLitige = rdvLeads.filter((l: any) => l.etat_rdv === 'Litige').length;
      const conversionRate = totalRdv > 0 ? (rdvValidated / totalRdv) * 100 : 0;

      // Trouver la date du dernier RDV
      const rdvDates = rdvLeads
        .map((l: any) => l.rdv_date)
        .filter((date: any) => date != null)
        .sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime());
      
      const lastRdvDate = rdvDates.length > 0 ? rdvDates[0] : null;

      return {
        client_id: profile.id,
        client_name: profile.client_name || profile.full_name || 'N/A',
        client_email: profile.email,
        totalCampaigns,
        activeCampaigns,
        totalRdv,
        rdvValidated,
        rdvPending,
        rdvNoShow,
        rdvLitige,
        conversionRate: parseFloat(conversionRate.toFixed(2)),
        lastRdvDate,
      };
    }) || [];
  },

  // Récupérer les statistiques par caller
  async getCallerStats(periodFilter?: 'all' | 'current_month' | 'current_week'): Promise<CallerStats[]> {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('id, full_name, client_name, email, updated_at')
      .eq('role', 'caller')
      .eq('is_active', true);

    if (error) throw error;

    // Calculer la date de début selon le filtre
    let startDate: Date | null = null;
    if (periodFilter && periodFilter !== 'all') {
      const now = new Date();
      if (periodFilter === 'current_month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      } else if (periodFilter === 'current_week') {
        const dayOfWeek = now.getDay();
        const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now);
        startDate.setDate(now.getDate() - diffToMonday);
        startDate.setHours(0, 0, 0, 0);
      }
    }

    // Récupérer les RDV pour chaque caller
    const statsPromises = profiles?.map(async (profile: any) => {
      let query = supabase
        .from('leads')
        .select('id, etat_rdv, created_at, rdv_date')
        .eq('rdv', true)
        .eq('caller_id', profile.id);

      // Ajouter le filtre de date si nécessaire
      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }

      const { data: callerRdv, error: rdvError } = await query;

      if (rdvError) {
        console.error(`Erreur RDV pour caller ${profile.id}:`, rdvError);
        return {
          caller_id: profile.id,
          caller_name: profile.full_name || 'N/A',
          caller_email: profile.email,
          totalRdvGenerated: 0,
          rdvValidated: 0,
          rdvPending: 0,
          rdvNoShow: 0,
          rdvLitige: 0,
          validationRate: 0,
          avgRdvPerDay: 0,
          lastActivity: profile.updated_at || '',
          lastRdvDate: null,
        };
      }

      const totalRdvGenerated = callerRdv?.length || 0;
      const rdvValidated = callerRdv?.filter((r: any) => r.etat_rdv === 'Validé').length || 0;
      const rdvPending = callerRdv?.filter((r: any) => r.etat_rdv === 'En attente').length || 0;
      const rdvNoShow = callerRdv?.filter((r: any) => r.etat_rdv === 'No show').length || 0;
      const rdvLitige = callerRdv?.filter((r: any) => r.etat_rdv === 'Litige').length || 0;
      const validationRate = totalRdvGenerated > 0 ? (rdvValidated / totalRdvGenerated) * 100 : 0;

      // Calculer la moyenne de RDV par jour
      let avgRdvPerDay = 0;
      if (totalRdvGenerated > 0) {
        const effectiveStartDate = startDate || new Date(Math.min(...callerRdv.map((r: any) => new Date(r.created_at).getTime())));
        const daysDiff = Math.max(1, Math.ceil((Date.now() - effectiveStartDate.getTime()) / (1000 * 60 * 60 * 24)));
        avgRdvPerDay = totalRdvGenerated / daysDiff;
      }

      // Trouver la date du dernier RDV
      const rdvDates = callerRdv
        ?.map((r: any) => r.rdv_date)
        .filter((date: any) => date != null)
        .sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime());
      
      const lastRdvDate = rdvDates && rdvDates.length > 0 ? rdvDates[0] : null;

      return {
        caller_id: profile.id,
        caller_name: profile.full_name || 'N/A',
        caller_email: profile.email,
        totalRdvGenerated,
        rdvValidated,
        rdvPending,
        rdvNoShow,
        rdvLitige,
        validationRate: parseFloat(validationRate.toFixed(2)),
        avgRdvPerDay: parseFloat(avgRdvPerDay.toFixed(2)),
        lastActivity: profile.updated_at || '',
        lastRdvDate,
      };
    }) || [];

    return Promise.all(statsPromises);
  },

  // Récupérer les commentaires sur les campagnes avec les réponses
  async getCampaignComments(): Promise<CampaignComment[]> {
    const { data, error } = await supabase
      .from('campaign_comments')
      .select(`
        id,
        campaign_id,
        author_id,
        author_name,
        text,
        comment_type,
        is_resolved,
        created_at,
        updated_at,
        campaigns!inner(nom),
        campaign_comment_responses(
          id,
          author_id,
          author_name,
          text,
          created_at
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const comments: CampaignComment[] = data?.map((comment: any) => ({
      id: comment.id,
      campaign_id: comment.campaign_id,
      campaign_name: comment.campaigns.nom,
      author_id: comment.author_id || '',
      author_name: comment.author_name,
      text: comment.text,
      comment_type: comment.comment_type as 'admin' | 'internal',
      is_resolved: comment.is_resolved || false,
      created_at: comment.created_at!,
      updated_at: comment.updated_at,
      responses: comment.campaign_comment_responses?.map((response: any) => ({
        id: response.id,
        author_id: response.author_id || '',
        author_name: response.author_name,
        text: response.text,
        created_at: response.created_at!,
      })) || [],
    })) || [];

    return comments;
  },

  // Ajouter un commentaire sur une campagne
  async addCampaignComment(
    campaignId: string,
    authorId: string,
    authorName: string,
    text: string,
    commentType: 'admin' | 'internal' = 'admin'
  ): Promise<void> {
    const { error } = await supabase
      .from('campaign_comments')
      .insert({
        campaign_id: campaignId,
        author_id: authorId,
        author_name: authorName,
        text,
        comment_type: commentType,
        is_resolved: false,
      });

    if (error) throw error;
  },

  // Marquer un commentaire comme résolu/non résolu
  async updateCommentResolution(commentId: string, isResolved: boolean): Promise<void> {
    const { error } = await supabase
      .from('campaign_comments')
      .update({ 
        is_resolved: isResolved,
        updated_at: new Date().toISOString()
      })
      .eq('id', commentId);

    if (error) throw error;
  },

  // Ajouter une réponse à un commentaire
  async addCommentResponse(
    commentId: string,
    authorId: string,
    authorName: string,
    text: string
  ): Promise<void> {
    const { error } = await supabase
      .from('campaign_comment_responses')
      .insert({
        comment_id: commentId,
        author_id: authorId,
        author_name: authorName,
        text,
      });

    if (error) throw error;
  },
};

// Exports pour la compatibilité avec les imports existants
export const getAllRdvWithDetails = adminService.getAllRdvWithDetails;
export const getAdminStats = adminService.getAdminStats;
export const getClientStats = adminService.getClientStats;
export const getCallerStats = adminService.getCallerStats;
export const getCampaignComments = adminService.getCampaignComments;
export const addCampaignComment = adminService.addCampaignComment;
export const updateCommentResolution = adminService.updateCommentResolution; 